
export interface Product {
    produtoId: number;
    codigo: string;
    codigoReferencia?: string;
    nome: string;
    codigoBarra?: string;
    marca: string;
    secao?: string;
    peso: number;
    preco: number;
    precoDeCusto: number;
    dataCriacao?: string;
    link?: string;
    descricaoDetalhada?: string;
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

/**
 * Foto do produto ou serviço. O arquivo NÃO tem URL pública: fica no disco do
 * servidor e é servido por endpoint autenticado, como o anexo de contato — por
 * isso a resposta traz só os metadados, e a imagem vem por `getImageFileUrl`.
 */
export interface ProductImage {
    imagemId: number;
    produtoId: number;
    contentType: string;
    nomeOriginal?: string;
    /** Posição na galeria. Zero é a capa. */
    ordem: number;
    dataCriacao?: string;
}

export interface CreateProductDTO {
    codigo: string;
    codigoReferencia?: string;
    nome: string;
    codigoBarra?: string;
    marca: string;
    secao?: string;
    peso: number;
    preco: number;
    precoDeCusto: number;
    link?: string;
    descricaoDetalhada?: string;
}

export interface UpdateProductDTO {
    produtoId: number;
    codigo: string;
    codigoReferencia?: string;
    nome: string;
    codigoBarra?: string;
    marca: string;
    secao?: string;
    peso: number;
    preco: number;
    precoDeCusto: number;
    link?: string;
    descricaoDetalhada?: string;
}

import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

export const ProductService = {
    getAll: async (): Promise<ApiResponse<Product[]>> => {
        const response = await apiFetch(`${API_URL}/api/Produto/BuscarTodos`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            if (response.status === 400 || response.status === 404) {
                return { sucesso: true, mensagem: 'Sem produtos', dados: [] };
            }
            throw new Error('Erro ao buscar produtos');
        }
        return response.json();
    },

    getById: async (id: number): Promise<ApiResponse<Product>> => {
        const response = await apiFetch(`${API_URL}/api/Produto/BuscarPorId/${id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error('Erro ao buscar produto');
        }
        return response.json();
    },

    create: async (product: CreateProductDTO): Promise<ApiResponse<Product>> => {
        const response = await apiFetch(`${API_URL}/api/Produto/Criar`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(product),
        });
        if (!response.ok) {
            throw new Error('Erro ao criar produto');
        }
        return response.json();
    },

    update: async (product: UpdateProductDTO): Promise<ApiResponse<Product>> => {
        const response = await apiFetch(`${API_URL}/api/Produto/Atualizar/${product.produtoId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(product),
        });
        if (!response.ok) {
            throw new Error('Erro ao atualizar produto');
        }
        return response.json();
    },

    delete: async (id: number): Promise<ApiResponse<any>> => {
        const response = await apiFetch(`${API_URL}/api/Produto/Deletar/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error('Erro ao excluir produto');
        }
        return response.json();
    },

    /* ─── Fotos do item ──────────────────────────────────────────── */

    getImages: async (produtoId: number): Promise<ProductImage[]> => {
        const response = await apiFetch(`${API_URL}/api/Produto/${produtoId}/Imagens`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
            // Item sem foto é resposta legítima, não falha de carregamento.
            if (response.status === 400 || response.status === 404) return [];
            throw new Error('Erro ao buscar as fotos do item');
        }
        const result: ApiResponse<ProductImage[]> = await response.json();
        return result.dados || [];
    },

    uploadImage: async (produtoId: number, file: File): Promise<ProductImage> => {
        const formData = new FormData();
        formData.append('Arquivo', file);

        const response = await apiFetch(`${API_URL}/api/Produto/${produtoId}/Imagens/Adicionar`, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) {
            let mensagem = 'Erro ao enviar a foto';
            try {
                const erro = await response.json();
                mensagem = erro.mensagem || mensagem;
            } catch { /* resposta sem corpo: fica a mensagem genérica */ }
            throw new Error(mensagem);
        }
        const result: ApiResponse<ProductImage> = await response.json();
        return result.dados;
    },

    deleteImage: async (imagemId: number): Promise<void> => {
        const response = await apiFetch(`${API_URL}/api/Produto/Imagens/${imagemId}/Remover`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            throw new Error('Erro ao remover a foto');
        }
    },

    /** A galeria inteira na ordem nova — o primeiro id é a capa. */
    reorderImages: async (produtoId: number, imagemIds: number[]): Promise<void> => {
        const response = await apiFetch(`${API_URL}/api/Produto/${produtoId}/Imagens/Ordem`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imagemIds }),
        });
        if (!response.ok) {
            throw new Error('Erro ao salvar a ordem das fotos');
        }
    },

    /**
     * O arquivo em si. O endpoint é autenticado, então não dá para apontar o
     * `<img src>` direto para ele: baixa e devolve um object URL — mesmo
     * arranjo do anexo de contato. Quem chama é responsável pelo `revokeObjectURL`.
     */
    getImageFileUrl: async (imagemId: number): Promise<string> => {
        const response = await apiFetch(`${API_URL}/api/Produto/Imagens/${imagemId}/Arquivo`, {
            method: 'GET',
        });
        if (!response.ok) {
            throw new Error('Erro ao obter a foto');
        }
        const blob = await response.blob();
        return URL.createObjectURL(blob);
    },
};
